export type WorkflowConditionBadgeType =
  | 'field'
  | 'question'
  | 'ai_label'
  | 'system'
  | 'visitor_id'
  | 'company_data'
  | 'account';

export interface WorkflowConditionField {
  key: string;
  label: string;
  badgeType: WorkflowConditionBadgeType;
  badgeLabel: string;
  type?: string;
  options?: string[];
  legacyKey?: string;
  description?: string;
}

type TaxonomyLikeField = {
  name: string;
  label?: string;
  question?: boolean;
  ai_label?: boolean;
  type?: string;
  options?: string[];
  description?: string;
};

const SYSTEM_FIELDS: WorkflowConditionField[] = [
  { key: 'visitor.id', label: 'Visitor ID', badgeType: 'visitor_id', badgeLabel: 'Visitor ID' },
  { key: 'system.first_seen_at', label: 'First Seen', badgeType: 'system', badgeLabel: 'System' },
  { key: 'system.last_seen_at', label: 'Last Seen', badgeType: 'system', badgeLabel: 'System' },
  { key: 'system.last_activity_at', label: 'Latest Aimdoc Interaction', badgeType: 'system', badgeLabel: 'System' },
  { key: 'system.latest_outcome_name', label: 'Latest Aimdoc Outcome', badgeType: 'system', badgeLabel: 'System' },
  { key: 'system.latest_outcome_label', label: 'Latest Aimdoc Outcome Label', badgeType: 'system', badgeLabel: 'System' },
  { key: 'system.latest_outcome_set_at', label: 'Latest Aimdoc Outcome Date', badgeType: 'system', badgeLabel: 'System' },
  { key: 'system.first_seen_url', label: 'First Seen URL', badgeType: 'system', badgeLabel: 'System' },
  { key: 'system.last_seen_url', label: 'Last Seen URL', badgeType: 'system', badgeLabel: 'System' },
  { key: 'system.referrer', label: 'Referrer URL', badgeType: 'system', badgeLabel: 'System' },
  { key: 'system.aimdoc_parent_url', label: 'Source URL', badgeType: 'system', badgeLabel: 'System', legacyKey: 'aimdoc_parent_url' },
  { key: 'system.aimdoc_referrer', label: 'Referrer', badgeType: 'system', badgeLabel: 'System', legacyKey: 'aimdoc_referrer' },
  { key: 'system.ip_address', label: 'IP Address', badgeType: 'system', badgeLabel: 'System' },
  { key: 'system.hostname', label: 'Hostname', badgeType: 'system', badgeLabel: 'System' },
  { key: 'system.city', label: 'City', badgeType: 'system', badgeLabel: 'System', legacyKey: 'city' },
  { key: 'system.region', label: 'Region', badgeType: 'system', badgeLabel: 'System', legacyKey: 'region' },
  { key: 'system.country', label: 'Country', badgeType: 'system', badgeLabel: 'System', legacyKey: 'country' },
  { key: 'system.postal', label: 'Postal Code', badgeType: 'system', badgeLabel: 'System' },
  { key: 'system.loc', label: 'Coordinates', badgeType: 'system', badgeLabel: 'System' },
  { key: 'system.org', label: 'Network Organization', badgeType: 'system', badgeLabel: 'System' },
  { key: 'system.timezone', label: 'Timezone', badgeType: 'system', badgeLabel: 'System' },
];

const PERSON_FIELDS: WorkflowConditionField[] = [
  { key: 'person.full_name', label: 'Full Name', badgeType: 'visitor_id', badgeLabel: 'Visitor ID' },
  { key: 'person.first_name', label: 'First Name', badgeType: 'visitor_id', badgeLabel: 'Visitor ID' },
  { key: 'person.last_name', label: 'Last Name', badgeType: 'visitor_id', badgeLabel: 'Visitor ID' },
  { key: 'person.email', label: 'Email', badgeType: 'visitor_id', badgeLabel: 'Visitor ID' },
  { key: 'person.title', label: 'Job Title', badgeType: 'visitor_id', badgeLabel: 'Visitor ID' },
  { key: 'person.linkedin_url', label: 'LinkedIn URL', badgeType: 'visitor_id', badgeLabel: 'Visitor ID' },
  { key: 'person.city', label: 'Person City', badgeType: 'visitor_id', badgeLabel: 'Visitor ID' },
  { key: 'person.state', label: 'Person State', badgeType: 'visitor_id', badgeLabel: 'Visitor ID' },
  { key: 'person.zipcode', label: 'Person Zip Code', badgeType: 'visitor_id', badgeLabel: 'Visitor ID' },
];

// Enrichment revenue is a range label, never a number, and the backend
// compares it as a string (`equals` / `in`), so a numeric input could never
// express a match. These are the canonical buckets: RB2B emits them
// directly and numeric providers (Apollo) are mapped onto them at ingest
// (identity_policy.revenue_bucket_label). "Above $50M" is RB2B's coarse top
// label for most large companies; the finer ranges after it come from Apollo
// (after a work email) and from RB2B's rarer scheme, so a top-end condition
// should include "Above $50M" plus the finer ranges it means to cover.
// Ordered low to high.
export const ESTIMATED_REVENUE_BUCKETS: string[] = [
  'Below $500k',
  '$500k - $1M',
  '$1M - $5M',
  '$5M - $10M',
  '$10M - $20M',
  '$20M - $50M',
  'Above $50M',
  '$50M - $100M',
  '$100M - $250M',
  '$250M - $500M',
  '$500M - $1B',
  '$1B - $2.5B',
  '$2.5B - $5B',
  'Over $5B',
  'Unknown',
];

const COMPANY_FIELDS: WorkflowConditionField[] = [
  { key: 'company.name', label: 'Company Name', badgeType: 'company_data', badgeLabel: 'Company Data' },
  { key: 'company.domain', label: 'Company Domain', badgeType: 'company_data', badgeLabel: 'Company Data' },
  { key: 'company.website_url', label: 'Company Website', badgeType: 'company_data', badgeLabel: 'Company Data' },
  { key: 'company.industry', label: 'Industry', badgeType: 'company_data', badgeLabel: 'Company Data' },
  { key: 'company.employee_count', label: 'Employee Count', badgeType: 'company_data', badgeLabel: 'Company Data', type: 'number' },
  {
    key: 'company.estimated_revenue',
    label: 'Estimated Revenue',
    badgeType: 'company_data',
    badgeLabel: 'Company Data',
    type: 'select',
    options: ESTIMATED_REVENUE_BUCKETS,
    description: 'Annual revenue range from visitor identification or enrichment. Use "in" to match several ranges.',
  },
  { key: 'company.linkedin_url', label: 'Company LinkedIn URL', badgeType: 'company_data', badgeLabel: 'Company Data' },
];

// Values are lifecycle stage KEYS (what Account.stage stores and what the
// backend compares against), not display labels. Callers that know the org's
// stages pass them so the value input becomes a dropdown; without them the
// field still exists as a free-text match.
function buildAccountFields(lifecycleStageKeys?: string[]): WorkflowConditionField[] {
  const hasStages = Array.isArray(lifecycleStageKeys) && lifecycleStageKeys.length > 0;
  return [
    {
      key: 'account.stage',
      label: 'Lifecycle Stage',
      badgeType: 'account',
      badgeLabel: 'Account',
      description: "The account's lifecycle stage (e.g. lead, opportunity, customer).",
      ...(hasStages ? { type: 'select', options: lifecycleStageKeys } : {}),
    },
  ];
}

function badgeForField(field: TaxonomyLikeField): Pick<WorkflowConditionField, 'badgeType' | 'badgeLabel'> {
  if (field.ai_label) return { badgeType: 'ai_label', badgeLabel: 'AI Label' };
  if (field.question) return { badgeType: 'question', badgeLabel: 'Question' };
  return { badgeType: 'field', badgeLabel: 'Field' };
}

export function buildWorkflowConditionFields(
  formFields: TaxonomyLikeField[],
  options?: { lifecycleStageKeys?: string[] },
): WorkflowConditionField[] {
  const taxonomyFields = (formFields || [])
    .filter((field) => field && typeof field.name === 'string' && field.name.trim())
    .map((field) => ({
      key: `form.${field.name}`,
      legacyKey: field.name,
      label: field.label || field.name,
      type: field.type,
      options: Array.isArray(field.options) ? field.options : undefined,
      description: field.description,
      ...badgeForField(field),
    }));

  return [
    ...taxonomyFields,
    ...SYSTEM_FIELDS,
    ...PERSON_FIELDS,
    ...COMPANY_FIELDS,
    ...buildAccountFields(options?.lifecycleStageKeys),
  ].sort((a, b) => a.label.localeCompare(b.label));
}

export function resolveWorkflowConditionField(
  fields: WorkflowConditionField[],
  fieldKey: string,
): WorkflowConditionField | undefined {
  return fields.find((field) => field.key === fieldKey || field.legacyKey === fieldKey);
}
